/**
 * Key Reconciliations tab: match notes table + flags table (or empty state).
 * Kept under 200 lines.
 */
import React from 'react';
import { Box, Center, HStack, Icon, Table, Tbody, Td, Text, Thead, Th, Tr, VStack } from '@chakra-ui/react';
import { TbFlag, TbInfoCircle } from 'react-icons/tb';
import type { InvoiceDetailResponse, ReconciliationMatchNote } from '../../types/invoice';
import { filterOutIgnoredTabCalculationMismatches } from '../../utils/invoiceIgnoredTabFlags';
import { InvoiceSubmittedNexPayloadSection } from './InvoiceSubmittedNexPayloadSection';
import type { NexReconciliationIdentifierMode } from '../../utils/invoiceNexIdentifierUi';
import { getInvoiceNexIdentifierUi } from '../../utils/invoiceNexIdentifierUi';
import {
    filterFlaggedPartsByPolicy,
    omitReturnSlipAmountFlags,
    type InvoiceMismatchFlagFamilyPolicy,
} from '../../utils/invoiceFlagFamilyPolicy';

const tableSx = {
    tableLayout: 'fixed' as const,
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    'thead tr:first-of-type th:first-of-type': { borderTopLeftRadius: '6px' },
    'thead tr:first-of-type th:last-of-type': { borderTopRightRadius: '6px' },
    'tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: '6px' },
    'tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: '6px' },
};

export interface InvoiceReconciliationsTabProps {
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    getStringValue: (field: keyof InvoiceDetailResponse) => string;
    getIssueTooltipText: (issue: any, hasLocalMatch?: boolean, asString?: boolean) => any;
    fmtUsd: (n: number) => string;
    openReconciliationDetailModal: (modal: any) => void;
    effectiveReconciliationMatchNotes?: ReconciliationMatchNote[];
    /** Current form totals (updates live before save). */
    liveGrandTotalVsAboveLineItemsWithFeesMismatch?: {
        extractedTotal: number;
        expectedFromAboveLineItems: number;
    } | null;
    /** Org invoicing setting: when true, freight is represented as a line item and hidden in AP header summaries. */
    freightAsLineItem?: boolean;
    /** Org invoicing setting: when true, US sales tax is an AP dist line and hidden in AP header summaries. */
    salesTaxAsLineItem?: boolean;
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    /** When Amount/Qty/Price families are disabled, hide those rows from the flags table. */
    mismatchFlagFamilyPolicy?: InvoiceMismatchFlagFamilyPolicy;
}

export function InvoiceReconciliationsTab(props: InvoiceReconciliationsTabProps) {
    const {
        invoice,
        editedFields,
        getStringValue,
        getIssueTooltipText,
        fmtUsd,
        openReconciliationDetailModal,
        effectiveReconciliationMatchNotes,
        liveGrandTotalVsAboveLineItemsWithFeesMismatch,
        freightAsLineItem = true,
        salesTaxAsLineItem = false,
        nexReconciliationIdentifierMode = 'ro',
        mismatchFlagFamilyPolicy,
    } = props;
    const idUi = React.useMemo(
        () => getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode),
        [nexReconciliationIdentifierMode]
    );
    const rlItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const restockingFeeItems = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const ignoredParts = editedFields.ignoredParts ?? invoice?.ignoredParts ?? [];
    const flaggedParts = omitReturnSlipAmountFlags(
        filterFlaggedPartsByPolicy(
            filterOutIgnoredTabCalculationMismatches(invoice?.reconciliationIssues?.flaggedParts, {
                lineItems: rlItems,
                freightItems,
                fuelItems,
                restockingFeeItems,
                ignoredParts,
            }),
            mismatchFlagFamilyPolicy ?? {
                amountMismatchFlaggingEnabled: true,
                quantityMismatchFlaggingEnabled: true,
                priceMismatchFlaggingEnabled: true,
            },
        ),
        invoice?.invoiceType,
    );
    const vendorMismatch = invoice?.reconciliationIssues?.vendorNameMismatch;
    const roNotFound = invoice?.reconciliationIssues?.roNumberNotFound;

    const flagRows: Array<{ description: string; partNumber: string; reason: React.ReactNode; partIndex?: number; issue?: any }> = [];
    const nexSubmissionError = invoice?.reconciliationIssues?.nexSubmissionError;

    if (nexSubmissionError) {
        flagRows.push({
            description: 'NEX Submission',
            partNumber: '—',
            reason: `${nexSubmissionError.title} (${nexSubmissionError.status || 'N/A'}): ${nexSubmissionError.detail}`,
        });
    }
    flaggedParts.forEach((issue: any) => {
        if (issue.partIndex < 0) {
            const tabIndex = -(issue.partIndex + 1);
            const scope = issue.issueDetails?.scope;

            if (issue.issueType === "restocking_fee_on_return") {
                const item = restockingFeeItems[tabIndex];
                flagRows.push({
                    description: item?.title || `Restocking Fee ${tabIndex + 1}`,
                    partNumber: issue.partNumber || item?.partNumber || 'restocking',
                    reason: getIssueTooltipText(issue),
                    partIndex: issue.partIndex,
                    issue,
                });
                return;
            }

            if (scope === "freight") {
                const item = freightItems[tabIndex];
                flagRows.push({
                    description: item?.title || `Freight ${tabIndex + 1}`,
                    partNumber: issue.partNumber || item?.partNumber || 'freight',
                    reason: getIssueTooltipText(issue),
                    partIndex: issue.partIndex,
                    issue,
                });
                return;
            }

            if (scope === "fuel") {
                const item = fuelItems[tabIndex];
                flagRows.push({
                    description: item?.title || `Fuel ${tabIndex + 1}`,
                    partNumber: issue.partNumber || item?.partNumber || 'fuel',
                    reason: getIssueTooltipText(issue),
                    partIndex: issue.partIndex,
                    issue,
                });
                return;
            }

            // Unknown negative-index scope: fall back to restocking fee tab data.
            const item = restockingFeeItems[tabIndex];
            flagRows.push({
                description: item?.title || `Restocking Fee ${tabIndex + 1}`,
                partNumber: issue.partNumber || item?.partNumber || 'restocking',
                reason: getIssueTooltipText(issue),
                partIndex: issue.partIndex,
                issue,
            });
        } else {
            const item = rlItems[issue.partIndex];
            flagRows.push({
                description: item?.title || `Line ${issue.partIndex + 1}`,
                partNumber: issue.partNumber || item?.partNumber || '—',
                reason: getIssueTooltipText(issue),
                partIndex: issue.partIndex,
                issue,
            });
        }
    });
    if (vendorMismatch) {
        const inv = vendorMismatch.invoiceVendorName || '—';
        const nexSingle = vendorMismatch.nexVendorName?.trim();
        const nexList = vendorMismatch.nexVendorNames?.length ? vendorMismatch.nexVendorNames.join(', ') : '';
        const nexReason =
            nexSingle && nexSingle.length > 0
                ? `vs NEX "${nexSingle}"`
                : nexList
                  ? `NEX vendor options: ${nexList}`
                  : 'No vendor names on this NEX purchase order';
        flagRows.push({ description: 'Vendor Name', partNumber: '—', reason: `Mismatch: "${inv}" ${nexReason}` });
    }
    if (roNotFound) {
        flagRows.push({
            description: idUi.flagTableDescription,
            partNumber: getStringValue('roNumber') || '—',
            reason: idUi.flagTableNotFoundReason,
        });
    }
    if (liveGrandTotalVsAboveLineItemsWithFeesMismatch) {
        const m = liveGrandTotalVsAboveLineItemsWithFeesMismatch;
        flagRows.push({
            description: 'Total vs above-line-items + fees',
            partNumber: '—',
            reason: `Total ${m.extractedTotal < 0 ? `-$${fmtUsd(Math.abs(m.extractedTotal))}` : `$${fmtUsd(m.extractedTotal)}`} ≠ above line items subtotal + extracted tax + discount + conditional header freight ${m.expectedFromAboveLineItems < 0 ? `-$${fmtUsd(Math.abs(m.expectedFromAboveLineItems))}` : `$${fmtUsd(m.expectedFromAboveLineItems)}`}`,
        });
    }
    if (invoice?.reconciliationIssues?.noExtractedLineItems) flagRows.push({ description: 'Line Items', partNumber: '—', reason: 'No items in any tab (0 line items, 0 freight, 0 ignored)' });

    const baseMatchNotes =
        effectiveReconciliationMatchNotes ??
        (invoice?.reconciliationIssues as {
            reconciliationMatchNotes?: Array<{
                partIndex: number;
                invoicePartNumber: string;
                nexPartNumber: string;
                reason: string;
            }>;
        } | undefined)?.reconciliationMatchNotes ??
        [];

    // Some rejected/returned parts are represented via rejectedInReturnsParts; ensure they always
    // surface as match notes even if reconciliationMatchNotes is missing or was not preserved.
    const rejectedInReturnsParts =
        (invoice?.reconciliationIssues as {
            rejectedInReturnsParts?: Array<{ partIndex: number; partNumber: string; tooltip?: string }>;
        } | undefined)?.rejectedInReturnsParts ?? [];

    const rejectedNotes = rejectedInReturnsParts.map((rej) => {
        const item = rlItems[rej.partIndex];
        return {
            partIndex: rej.partIndex,
            invoicePartNumber: item?.partNumber ?? rej.partNumber,
            nexPartNumber: rej.partNumber,
            reason: rej.tooltip ?? 'Part was rejected/returned by the customer (found in NEX returns for this RO).',
        };
    });

    const matchNotes = [
        ...baseMatchNotes,
        ...rejectedNotes.filter(
            (rn) =>
                !baseMatchNotes.some(
                    (bn) =>
                        bn.partIndex === rn.partIndex &&
                        bn.invoicePartNumber === rn.invoicePartNumber &&
                        bn.nexPartNumber === rn.nexPartNumber &&
                        bn.reason === rn.reason
                )
        ),
    ];

    const showSubmittedNexPayload =
        (invoice?.status === 'submitted' || invoice?.status === 'manually_submitted') &&
        invoice?.nexSubmission?.lastStatus === 'succeeded';
    const showFailedNexAttemptPayload =
        invoice?.status === 'flagged' &&
        invoice?.nexSubmission?.lastStatus === 'failed' &&
        !!invoice?.nexSubmission?.lastAttemptedApInvoicePayload;

    if (flagRows.length === 0 && matchNotes.length === 0 && !showSubmittedNexPayload && !showFailedNexAttemptPayload) {
        return (
            <Center py={20}>
                <Text color="black" fontWeight="medium">No reconciliations to show! 🎉</Text>
            </Center>
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            {(showSubmittedNexPayload || showFailedNexAttemptPayload) && (
                <InvoiceSubmittedNexPayloadSection
                    invoice={invoice}
                    fmtUsd={fmtUsd}
                    freightAsLineItem={freightAsLineItem}
                    salesTaxAsLineItem={salesTaxAsLineItem}
                />
            )}
            {matchNotes.length > 0 && (
                <Box>
                    <HStack spacing={2} mb={3}>
                        <Icon as={TbInfoCircle as React.ElementType} boxSize={4} color="blue.500" />
                        <Text color="gray.900" fontSize="sm" fontWeight="bold">Match Notes ({matchNotes.length})</Text>
                    </HStack>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <Table variant="simple" size="sm" sx={tableSx}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th py={3} w="25%" color="gray.500" borderColor="gray.200" fontSize="xs">Invoice Part</Th>
                                    <Th py={3} w="25%" color="gray.500" borderColor="gray.200" fontSize="xs">NEX Part</Th>
                                    <Th py={3} w="50%" color="gray.500" borderColor="gray.200" fontSize="xs">Reason</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {matchNotes.map((note, idx) => (
                                    <Tr
                                        key={`match-note-${idx}`}
                                        _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                                        onClick={() =>
                                            openReconciliationDetailModal({
                                                type: 'match_note',
                                                matchNote: {
                                                    invoicePartNumber: note.invoicePartNumber,
                                                    nexPartNumber: note.nexPartNumber,
                                                    reason: note.reason,
                                                    partIndex: note.partIndex,
                                                },
                                            })
                                        }
                                    >
                                        <Td py={3} fontSize="sm" fontFamily="mono" color="gray.900" borderColor="gray.100">{note.invoicePartNumber}</Td>
                                        <Td py={3} fontSize="sm" fontFamily="mono" color="gray.900" borderColor="gray.100">{note.nexPartNumber}</Td>
                                        <Td py={3} fontSize="sm" color="gray.600" borderColor="gray.100">{note.reason}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
            )}
            {flagRows.length > 0 && (
                <Box>
                    <HStack spacing={2} mb={3}>
                        <Icon as={TbFlag as React.ElementType} boxSize={4} color="orange.500" />
                        <Text color="gray.900" fontSize="sm" fontWeight="bold">Flags ({flagRows.length})</Text>
                    </HStack>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <Table variant="simple" size="sm" sx={tableSx}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th py={3} w="30%" color="gray.500" borderColor="gray.200" fontSize="xs">Description</Th>
                                    <Th py={3} w="25%" color="gray.500" borderColor="gray.200" fontSize="xs">Part Number</Th>
                                    <Th py={3} w="45%" color="gray.500" borderColor="gray.200" fontSize="xs">Reason</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {flagRows.map((row, idx) => {
                                    const isClickable = row.partIndex != null && row.issue?.issueType !== 'missing_in_nex';
                                    return (
                                        <Tr
                                            key={`flag-${idx}`}
                                            _hover={isClickable ? { bg: 'gray.50', cursor: 'pointer' } : { bg: 'gray.50' }}
                                            onClick={isClickable ? () => openReconciliationDetailModal({ type: 'flag', partIndex: row.partIndex, issue: row.issue }) : undefined}
                                        >
                                            <Td py={3} fontSize="sm" color="gray.900" borderColor="gray.100">{row.description}</Td>
                                            <Td py={3} fontSize="sm" color="gray.900" borderColor="gray.100">{row.partNumber}</Td>
                                            <Td py={3} fontSize="sm" color="gray.600" borderColor="gray.100">{row.reason}</Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
            )}
        </VStack>
    );
}
